import { useMemo, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useUiStore } from '@/stores/ui';
import { useValidatorsStore } from '@/stores/validators';
import { useTasksStore } from '@/stores/tasks';
import { useProposalsStore } from '@/stores/proposals';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { AlertTriangle, CheckCircle, File as FileIcon, Loader2, RefreshCw, Sparkles, AlertCircle as AlertCircleIcon } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import type { ValidatorResults } from '@/core/state-shape';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { toast } from 'sonner';
import useSyncStore from '@/stores/sync';
import { getValidationSummary } from '@/lib/validation-summary';

// Helper to robustly extract numeric value from inconsistent data structure
const getNumericValue = (value: any): number | null => {
  if (typeof value === 'number') {
    return value;
  }
  if (value && typeof value === 'object' && typeof value.parsedValue === 'number') {
    return value.parsedValue;
  }
  return null;
};


type Failure = {
  type: 'Lint' | 'Typecheck' | 'Test';
  status: 'failed' | 'flaky';
  name: string;
  file: string;
  line?: number;
  message: string;
};

const SummaryCard = ({ title, results, category }: { title: string; results: any; category: 'lint' | 'typecheck' | 'tests' }) => {
  const data = results?.[category];
  const passed = data?.passed || 0;
  const failed = data?.failed || 0;
  const skipped = data?.skipped || 0;
  const flaky = data?.flaky || 0;

  if (!data) {
    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">{title}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="text-2xl font-bold">N/A</div>
                <p className="text-xs text-muted-foreground">Not run</p>
            </CardContent>
        </Card>
    );
  }

  return (
    <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">{title}</CardTitle>
            {failed > 0 ? <AlertTriangle className="h-4 w-4 text-destructive" /> : (passed > 0 || (data && failed === 0)) && <CheckCircle className="h-4 w-4 text-green-500" />}
        </CardHeader>
        <CardContent>
            <div className="text-2xl font-bold">{passed} <span className="text-lg font-normal text-muted-foreground">Passed</span></div>
            <div className="flex space-x-2 text-xs text-muted-foreground">
                {failed > 0 && <span className="text-destructive">{failed} failed</span>}
                {flaky > 0 && <span>{flaky} flaky</span>}
                {skipped > 0 && <span>{skipped} skipped</span>}
            </div>
        </CardContent>
    </Card>
  )
};

export default function ValidatorsPage() {
    const [searchParams] = useSearchParams();
    const taskId = searchParams.get('taskId');
    const runIdFromParams = searchParams.get('runId');
    const navigate = useNavigate();

    const setHeaderTitle = useUiStore(state => state.setHeaderTitle);
    useEffect(() => {
        setHeaderTitle('Validators');
    }, [setHeaderTitle]);

    const { results, run, poll, runFullCoverage } = useValidatorsStore();
    const progress = useValidatorsStore(s => s.progress);
    const { byId: tasksById } = useTasksStore();
    const applyProposal = useProposalsStore(s => s.apply);

    const task = taskId ? tasksById[taskId] : undefined;
    const taskResults = taskId ? results.byTask[taskId] : results.latest;

    const activeRun = useMemo(() => {
        // Prefer runId from URL if present
        if (runIdFromParams) {
            const run = progress[runIdFromParams];
            if (run?.status === 'running') return run;
        }
        // Fallback to finding the latest running task for the given taskId
        if (taskId) {
            const taskProgress = Object.values(progress)
                .filter(p => p.taskId === taskId && p.status === 'running')
                .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
            return taskProgress.length > 0 ? taskProgress[0] : null;
        }
        return null;
    }, [taskId, runIdFromParams, progress]);

    useEffect(() => {
        let interval: number | undefined;
        if (activeRun?.id) {
            const pollFn = () => {
                const currentRun = useValidatorsStore.getState().progress[activeRun.id];
                if (currentRun?.status !== 'running') {
                    clearInterval(interval!);
                  useSyncStore.getState().fetchSnapshot();
                } else poll(activeRun.id);

                
            };
            interval = setInterval(pollFn, 2000);
        }
        return () => {
            if (interval) clearInterval(interval!);
        }
    }, [activeRun, poll]);

    const allFailures = useMemo((): Failure[] => {
        if (!taskResults) return [];
        const failures: Failure[] = [];
        const processCategory = (categoryResults: any, typeName: Failure['type']) => {
            if (!categoryResults?.failures) return;
            categoryResults.failures.forEach((f: any) => {
                failures.push({
                    type: typeName,
                    status: 'failed',
                    name: f.rule || (typeName === 'Test' ? f.test : `${f.type || typeName} Error`),
                    file: f.file || (typeName === 'Test' ? f.suite : 'unknown'),
                    line: f.line,
                    message: f.message || f.error,
                });
            });
        };
        processCategory(taskResults.lint, 'Lint');
        processCategory(taskResults.typecheck, 'Typecheck');
        processCategory(taskResults.tests, 'Test');
        return failures;
    }, [taskResults]);

    const failuresByFile = useMemo(() => {
        const grouped: Record<string, Failure[]> = {};
        for (const failure of allFailures) {
            if (!grouped[failure.file]) {
                grouped[failure.file] = [];
            }
            grouped[failure.file].push(failure);
        }
        return Object.entries(grouped).sort(([pathA], [pathB]) => pathA.localeCompare(pathB));
    }, [allFailures]);

    const isRunComplete = taskResults?.status === 'complete' || taskResults?.status === 'failed';

    const coverageData = useMemo(() => {
      if (!taskResults?.tests?.coverage) return null;
      return Object.values(taskResults.tests.coverage);
    }, [taskResults]);

    const hasFailures = useMemo(() => {
        if (!taskResults) return false;
        // A run has failures if any category reports a failed status, or if there are detailed failure messages.
        return taskResults.lint?.status === 'failed' || taskResults.typecheck?.status === 'failed' || taskResults.tests?.status === 'failed' || allFailures.length > 0;
    }, [taskResults, allFailures]);

    const validationSummary = useMemo(
      () => getValidationSummary(taskResults, activeRun ?? undefined, task?.status),
      [taskResults, activeRun, task?.status]
    );

    const handleRerunImpacted = async () => {
        if (!taskId) return;
        await run({ taskId, scope: 'impacted' });
    };

    const handleRerunAll = async () => {
        if (!taskId) return;
        await run({ taskId, scope: 'all' });
    };

    const handleRunFullCoverage = async () => {
      const runId = await runFullCoverage();
      toast.info("Project-wide coverage scan started. Results will appear in the workspace when complete.");
    }

    const taskIsApplied = useMemo(() => {
        return task?.status === 'complete' || task?.status === 'merged';
    }, [task]);

    const handleProposeFix = (failure: Failure) => {
        const instruction = `A validation check failed. Please fix the following issue:\n\nType: ${failure.type}\nFile: ${failure.file}${failure.line ? `:${failure.line}` : ''}\nRule/Test: ${failure.name}\nMessage: ${failure.message}`;
        navigate(`/pages/task-new/?from=validators&instruction=${encodeURIComponent(instruction)}&scope=${encodeURIComponent(failure.file)}`);
    };

    const handleApply = async () => {
        if (!task || !task.currentProposalId) return;
        const success = await applyProposal(task.id, task.currentProposalId);
        if (success) {
            navigate(`/pages/workspace/`);
        }
    };

    const ApplyButton = ({ className = '' }: { className?: string }) => {
      if (!isRunComplete || taskIsApplied) return null;

      if (task?.status === 'validated' && validationSummary.status !== 'passed') {
        return null;
      }

      if (hasFailures) {
        return (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="secondary" className={className}>
                <AlertCircleIcon className="mr-2 h-4 w-4" />
                Apply with Failures
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Apply with failures?</AlertDialogTitle>
                <AlertDialogDescription>
                  This proposal has validation failures. Applying these changes may introduce errors into your codebase. Are you sure you want to continue?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleApply}>Apply Anyway</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        );
      }

      return (
        <Button onClick={handleApply} className={className}>
            <CheckCircle className="mr-2 h-4 w-4" />
            Apply Changes
        </Button>
      );
    }

    return (
        <div className="container mx-auto p-4 lg:p-6 space-y-6 overflow-auto">
            <header className="flex flex-col gap-4 md:flex-row md:flex-wrap md:items-center md:justify-between">
                <div className="md:hidden">
                    <h1 className="text-xl font-bold">Validators</h1>
                    <p className="text-sm text-muted-foreground">
                        Results for task: <span className="font-semibold text-foreground">{task?.title || taskId || 'Latest Run'}</span>
                    </p>
                </div>
                <div className="hidden md:block">
                    <h1 className="text-2xl font-bold">Validators</h1>
                    <p className="text-muted-foreground">
                        Results for task: <span className="font-semibold text-foreground">{task?.title || taskId || 'Latest Run'}</span>
                    </p>
                </div>
                <div className="flex w-full flex-col gap-2 sm:flex-row sm:flex-wrap sm:justify-end">
                    <Button variant="outline" onClick={handleRunFullCoverage} disabled={!!activeRun} className="w-full sm:w-auto">
                      <Sparkles className="mr-2 h-4 w-4" />
                      Scan Project Coverage
                    </Button>
                    <ApplyButton className="w-full sm:w-auto" />
                    <Button variant="outline" onClick={handleRerunImpacted} disabled={!!activeRun} className="w-full sm:w-auto">
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Rerun Impacted
                    </Button>
                    {taskId && <Button onClick={handleRerunAll} disabled={!!activeRun} className="w-full sm:w-auto">
                        <RefreshCw className="mr-2 h-4 w-4" />
                        Run All
                    </Button>}
                </div>
            </header>

            {activeRun ? (
                <Card>
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            Validators are running...
                        </CardTitle>
                        <CardDescription className="capitalize">
                           Phase: {activeRun.phase?.replace(/_/g, ' ') || 'Initializing'}
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Progress value={activeRun.percent || 0} className="w-full" />
                    </CardContent>
                </Card>
            ) : (
                <>
                    <div className="grid gap-4 md:grid-cols-3">
                        {taskResults?.lint && (
                            <SummaryCard title="Lint" results={taskResults} category="lint" />
                        )}
                        {taskResults?.typecheck && (
                            <SummaryCard title="Typecheck" results={taskResults} category="typecheck" />
                        )}
                        {taskResults?.tests && (
                            <SummaryCard title="Tests" results={taskResults} category="tests" />
                        )}
                    </div>

                    <Tabs defaultValue="failures" className="w-full">
                      <div className="overflow-x-auto">
                        <TabsList className="w-full justify-start">
                          <TabsTrigger value="failures" className="flex-1 sm:flex-none">Failures ({allFailures.length || (taskResults?.tests?.failed ?? 0)})</TabsTrigger>
                          <TabsTrigger value="coverage" disabled={!coverageData || coverageData.length === 0} className="flex-1 sm:flex-none">Coverage</TabsTrigger>
                        </TabsList>
                      </div>

                      <TabsContent value="failures">
                        {allFailures.length > 0 ? (
                            <div className="space-y-2 mt-4">
                                {failuresByFile.map(([filePath, failures]) => (
                                    <Card key={filePath} className="overflow-hidden">
                                        <CardHeader className="flex flex-row items-center justify-between p-4 bg-muted/50">
                                            <div className="flex items-center gap-3">
                                                <FileIcon className="h-4 w-4" />
                                                <span className="font-mono text-sm font-semibold">{filePath}</span>
                                            </div>
                                            <Badge variant="destructive">{failures.length} issue(s)</Badge>
                                        </CardHeader>
                                        <CardContent className="p-0">
                                            <div className="overflow-x-auto">
                                                <Table>
                                                    <TableHeader>
                                                        <TableRow><TableHead className="w-[120px]">Type</TableHead><TableHead>Details</TableHead><TableHead className="text-right">Actions</TableHead></TableRow>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {failures.map((f, index) => (
                                                            <TableRow key={index}><TableCell><Badge variant="outline">{f.type}</Badge></TableCell><TableCell><div className="font-semibold">{f.name}</div><div className="text-xs text-muted-foreground"><span className="font-mono">{f.file.split('/').pop()}{f.line ? `:${f.line}` : ''}</span> - {f.message}</div></TableCell><TableCell className="text-right"><Button variant="ghost" size="sm" onClick={() => handleProposeFix(f)}><Sparkles className="mr-2 h-4 w-4" />Propose Fix</Button></TableCell></TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                            </div>
                        ) : isRunComplete && hasFailures ? (
                            <div className="text-center py-12 text-muted-foreground">
                                <AlertTriangle className="mx-auto h-12 w-12 text-destructive" />
                                <p className="mt-4 font-semibold">Validation Failed</p>
                                <p className="mt-1">The validator reported failures, but no specific details were provided.</p>
                            </div>
                        ) : (
                            <div className="text-center py-12 text-muted-foreground">
                                <CheckCircle className="mx-auto h-12 w-12 text-green-500" />
                                <p className="mt-4 font-semibold">Everything looks good!</p>
                            </div>
                        )}
                      </TabsContent>

                      <TabsContent value="coverage">
                        <Card className="mt-4">
                            <CardHeader>
                                <CardTitle>Test Coverage Report</CardTitle>
                                <CardDescription>Line, function, and branch coverage for modified files in this run.</CardDescription>
                            </CardHeader>
                            <CardContent>
                                {coverageData && coverageData.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <Table>
                                            <TableHeader>
                                                <TableRow>
                                                    <TableHead>File</TableHead>
                                                    <TableHead className="text-right">Lines</TableHead>
                                                    <TableHead className="text-right">Functions</TableHead>
                                                    <TableHead className="text-right">Branches</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {coverageData.map(cov => (
                                                    <TableRow key={cov.path}>
                                                        <TableCell className="font-mono">{cov.path}</TableCell>
                                                        <TableCell className="text-right tabular-nums">{getNumericValue(cov.summary.lines?.pct)?.toFixed(1) ?? 'N/A'}%</TableCell>
                                                        <TableCell className="text-right tabular-nums">{getNumericValue(cov.summary.functions?.pct)?.toFixed(1) ?? 'N/A'}%</TableCell>
                                                        <TableCell className="text-right tabular-nums">{getNumericValue(cov.summary.branches?.pct)?.toFixed(1) ?? 'N/A'}%</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </div>
                                ) : (
                                    <div className="text-center py-8 text-muted-foreground">
                                        <p>No coverage data available for this run.</p>
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                      </TabsContent>
                    </Tabs>
                </>
            )}
        </div>
    );
}